import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthorization } from '@/features/authorization/authorizationHooks'
import { homePathForPermissions } from '@/features/auth/homeRouting'

/**
 * First-time user welcome page
 * Shows when user has never logged in before (last_login === null in Auth0)
 * Implements design from Figma (node-id=387-6691)
 * @returns {JSX.Element} The first-time welcome component
 */
const FirstTimeWelcome: React.FC = () => {
  const navigate = useNavigate()
  const { permissions } = useAuthorization()

  const handleGetStarted = () => {
    // Route to the permission-aware home: org-less case-only users go to /cases
    // (their only surface), everyone with order access goes to /orders.
    navigate(homePathForPermissions(permissions))
  }

  return (
    <div className="relative flex h-screen flex-col items-center justify-center overflow-y-auto bg-[#fafafa] py-8">
      {/* Logo */}
      <div className="mb-12 flex w-full max-w-[610px] justify-center px-8">
        <img
          src="/icons/Inflection_Logo_Dark_LG.png"
          alt="Inflection Medicine"
          className="h-14 w-auto"
        />
      </div>

      {/* Welcome Card */}
      <div className="relative z-10 w-full max-w-[610px] px-8">
        <div className="rounded-base border border-border-base bg-white p-8 shadow-[0px_25px_50px_-12px_rgba(29,41,61,0.25)]">
          {/* Welcome Icon */}
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-50">
              <svg
                className="h-8 w-8 text-brand-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          {/* Welcome Text */}
          <h1 className="mb-3 text-center text-2xl font-semibold leading-8 tracking-tight text-text-heading">
            Welcome to Inflection Medicine
          </h1>

          <p className="mb-8 text-center text-base leading-6 text-text-body">
            Your account has been created successfully. Get started by placing your first order.
          </p>

          {/* Get Started Button */}
          <button
            type="button"
            onClick={handleGetStarted}
            className="inline-flex w-full items-center justify-center gap-2 rounded-base bg-brand-600 px-4 py-2.5 text-base font-medium text-white shadow-xs transition-colors hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            <span>Get Started</span>
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* DNA Wave Pattern at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[503px] overflow-hidden">
        <img
          src="/icons/INFL Wave B 2026 - on dark.svg"
          alt=""
          className="h-full w-full object-cover"
          aria-hidden="true"
        />
      </div>
    </div>
  )
}

export default FirstTimeWelcome
